import React, { useState, useEffect, useCallback, useRef  } from "react";
import Header from "../components/Header";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import axios from "axios";
import "../css/StockistPOBSOBTarget.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";

const StockistPOBSOBTarget = () => {
    const navigate = useNavigate();

    // State for dropdown options
    const [states, setStates] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [employees, setEmployees] = useState([]);
    
    // State for selected filters
    const [selectedState, setSelectedState] = useState("");
    const [selectedDivision, setSelectedDivision] = useState("All");
    const [selectedEmployee, setSelectedEmployee] = useState("All");
    
    // State for table data and UI
    const [tableData, setTableData] = useState([]);
    const [showTable, setShowTable] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const tableRef = useRef();


    // Month/Year dropdown setup
    const getMonthYearOptions = () => {
        const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const currentYear = new Date().getFullYear();
        return months.map((month, index) => ({
        label: `${month} ${currentYear}`,
        value: `${String(index + 1).padStart(2, '0')}-${currentYear}`
        }));
    };
    
    const monthYearOptions = getMonthYearOptions();
    const [pobMonthYear, setPobMonthYear] = useState(monthYearOptions[new Date().getMonth()].value);

    

    useEffect(() => {
        const fetchStates = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() });
                setStates(response.data);
            } catch (err) {
                console.error("Error fetching states:", err);
                setError("Failed to load states");
            }
        };
        fetchStates();
    }, []);

    const fetchEmployees = useCallback(async (stateId, divisionName) => {
        if (!stateId) return;
      
        try {
          let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
          if (divisionName && divisionName !== "All") {
            url += `&division=${encodeURIComponent(divisionName)}`;
          }
      
          const res = await axios.get(url, { headers: getAuthHeaders() });
          setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...res.data]);
          setSelectedEmployee("All");
        } catch (err) {
          console.error("Error fetching employees:", err);
          setEmployees([]);
        }
      }, []);

    // Fetch divisions when state changes
    useEffect(() => {
        const fetchDivisions = async () => {
            if (selectedState) {
                try {
                    const response = await axios.get(`${BASE_URL}/api/divisions?state_id=${selectedState}`, { headers: getAuthHeaders() });
                    setDivisions([{ id: "All", name: "All" }, ...response.data]);
                    setSelectedDivision("All");
                } catch (err) {
                    console.error("Error fetching divisions:", err);
                    setError("Failed to load divisions");
                }
            } else {
                setDivisions([]);
                setSelectedDivision("All");
            }
        };
        fetchDivisions();
    }, [selectedState]);

    const handleStateChange = async (stateId) => {
        setSelectedState(stateId);
        setSelectedDivision("All");
        setSelectedEmployee("All");
    
        // fetch divisions
        try {
            const divisionRes = await axios.get(`${BASE_URL}/api/divisions?state_id=${stateId}`, { headers: getAuthHeaders() });
            setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
        } catch (err) {
            console.error("Error loading divisions", err);
        }
    
        // fetch employees based only on state
        fetchEmployees(stateId, "All");
    };
  
    const handleDivisionChange = async (divisionId) => {
        setSelectedDivision(divisionId);
        setSelectedEmployee("All");
    
        const division = divisions.find(d => d.id == divisionId);
        const divisionName = division?.name || "All";
    
        fetchEmployees(selectedState, divisionName);
    };

const handleGoClick = async () => {
  try {
    setIsLoading(true);
    setError(null);

    // Parse month and year from pobMonthYear (format: "MM-YYYY")
    const [month, year] = pobMonthYear.split('-').map(Number);

    let url = `${BASE_URL}/api/stockist/listwithtarget?state_id=${selectedState}&month=${month}&year=${year}`;

    if (selectedDivision && selectedDivision !== "All") {
      const division = divisions.find(d => d.id == selectedDivision);
      if (division?.name) url += `&division=${division.name}`;
    }

    if (selectedEmployee && selectedEmployee !== "All") {
      url += `&employee_id=${selectedEmployee}`;
    }

    const response = await axios.get(url, { headers: getAuthHeaders() });
    setTableData(response.data);
    setShowTable(true);
  } catch (err) {
    console.error("Failed to fetch summary", err);
    setError("Failed to load data. Please try again.");
  } finally {
    setIsLoading(false);
  }
};

    useEffect(() => {
        if (selectedState) { // Only fetch if a state is selected
            handleGoClick();
        }
    }, [pobMonthYear]);

  const handlePrint = () => {
    const printContent = tableRef.current.innerHTML;
  
    const printWindow = window.open("", "_blank", "width=900,height=600");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Table</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 14px;
              }
              th, td {
                border: 1px solid #ccc;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #f2f2f2;
              }
            </style>
          </head>
          <body>
            <h2>Stockist Data</h2>
            ${printContent}
            <script>
              window.onload = function() {
                window.print();
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };
  
  const handleExport = () => {
    const XLSX = require("xlsx");
  
    const ws = XLSX.utils.table_to_sheet(tableRef.current.querySelector("table"));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "StockistData");
  
    XLSX.writeFile(wb, "stockist_data.xlsx");
  };
  



  return (
    <div className="stockistPOBSOBTaregt-main">
      <Header />
      <Navbar />

      <div className="stockistPOBSOBTaregt-filter-container">
        <h1 class="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4"><span class="text-blue-700"> Select </span><span class="text-blue-500">Employee</span></h1>

        <div className="stockistPOBSOBTaregt-filter-row1">
          <label className="stockistPOBSOBTaregt-label">State:</label>
          <select
            className="stockistPOBSOBTaregt-dropdown"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="stockistPOBSOBTaregt-label">Division:</label>
          <select
            className="stockistPOBSOBTaregt-dropdown"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="stockistPOBSOBTaregt-filter-row2">
          <label className="stockistPOBSOBTaregt-label">Employee:</label>
          <select
            className="stockistPOBSOBTaregt-dropdown"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All" ? "All" : `${emp.first_name} ${emp.last_name} | ${emp.designation} | ${emp.headquarter_name} | ${emp.division} | ${emp.state_name}`}
              </option>
            ))}
          </select>

          <label className="stockistPOBSOBTaregt-label">Month / Year:</label>
          <select
            className="stockistPOBSOBTaregt-dropdown"
            value={pobMonthYear}
            onChange={(e) => setPobMonthYear(e.target.value)}
            // disabled={isLoading}
          >
            {monthYearOptions.map((option, idx) => (
              <option key={idx} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button 
          className="stockistPOBSOBTaregt-go-button" 
          onClick={handleGoClick}
          disabled={isLoading || !selectedState}
        >
          {isLoading ? "Loading..." : "GO"}
        </button>

        {error && <div className="stockistPOBSOBTaregt-error">{error}</div>}
      </div>
      {showTable && (
                <>
                    {/* Table container */}
                    <div className="stockistPOBSOBTaregt-table-container">
                    <div className="flex items-center justify-center mb-6">
                        <h2 className="text-center">
                        {states.find(s => s.id == selectedState)?.name || selectedState} - All Assigned Stockist
                        </h2>
                    </div>

                    <div className="stockistPOBSOBTaregt-table-wrapper" ref={tableRef}>
                        <table className="stockistPOBSOBTaregt-table">
                        <thead className="text-white">
                            <tr>
                            <th>S.No</th>
                            <th>State</th>
                            <th>Emp ID</th>
                            <th>Employee</th>
                            <th>Designation</th>
                            <th>Division</th>
                            <th>Manager</th>
                            <th>Stockist Code</th>
                            <th>Stockist</th>
                            <th>HQ</th>
                            <th>City</th>
                            <th>Phone</th>
                            <th>Target</th>
                            <th>POB</th>
                            <th>SOB</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((item, index) => (
                            <tr key={item.stockist_code}>
                                <td>{index + 1}</td>
                                <td>{item.state_name}</td>
                                <td>{item.employee_id}</td>
                                <td>{item.employee_name}</td>
                                <td>{item.employee_designation}</td>
                                <td>{item.employee_division}</td>
                                <td>{item.employee_manager}</td>
                                <td>{item.stockist_code}</td>
                                <td>{item.stockist_name}</td>
                                <td>{item.headquarter_name}</td>
                                <td>{item.city_name}</td>
                                <td>{item.mobile}</td>
                                <td>{item.sob_target}</td>
                                <td>{item.pob_monthly}</td>
                                <td>{item.sob_achieved}</td>
                            </tr>
                            ))}
                        </tbody>
                        </table>
                    </div>
                    </div>

                    {/* ✅ Buttons outside the table container */}
                    <div className="stockistPOBSOBTaregt-button-group">
                    <button className="stockistPOBSOBTaregt-print-btn px-4 py-2 bg-green-500 hover:bg-green-700 rounded-md font-bold cursor-pointer w-10%" onClick={handlePrint}>Print</button>
                    <button className="stockistPOBSOBTaregt-export-btn px-4 py-2 bg-teal-500 hover:bg-teal-700 rounded-md font-bold cursor-pointer w-10%" onClick={handleExport}>Export to Excel</button>
                    </div>
                </>
                )}



      <Footer />
    </div>
  );
};

export default StockistPOBSOBTarget;
